type ValueAtIndex<
  T extends Record<string, unknown> | string | number,
  P extends T,
> = { value: P; index: number };

type ReplaceInArrayOptions<
  T extends Record<string, unknown> | string | number,
  P extends T,
> = {
  readonly strictUniqueness?: boolean;
  readonly replaceAll?: boolean;
  readonly insert: T | ((v: P) => T);
  readonly predicate: ((v: T) => v is P) | ((v: T) => boolean);
};

/**
 * Replaces the provided 'value' in the array, {@link T[]}, if the 'value' exists in the array.
 * Returns the potentially updated array with an indication of whether or not the value was
 * replaced.
 *
 * @template {T} Element The type of the elements inside of the array.
 *
 * @param {T[]} arr The array for which the value should be appended to or replaced in.
 *
 * @param {ReplaceInArrayOptions<T, P>} options
 *   Options that govern the replacement.  There are four options:
 *
 *   1. @param {T | ((v: P) => T)} insert
 *
 *      The value that should be inserted into the array in place of a potentially existing value.
 *      If the value being inserted into the array {@link T[]}, depends on the potentially existing
 *      value in the array, {@link T}, this parameter can be provided as a callback.
 *
 *      Required
 *
 *   2. @param {(v: T) => boolean} predicate
 *
 *      Indicates whether or not the value exists in the array.
 *      Required
 *
 *   3. @param {boolean} strictUniqueness:
 *
 *      If specified as 'false' explicitly, a predicate that matches multiple values in the array
 *      will result in either the first of those values being replaced (if the 'replaceAll' option
 *      is 'false') or all of those values being replaced (if the 'replaceAll' option is 'true').
 *      Otherwise, a predicate that matches multiple values will result in an error being thrown.
 *
 *      Default: true
 *      Optional
 *
 *   4. @param {boolean} replaceAll:
 *
 *      If specified as 'true' explicitly, and 'strictUniqueness' is false, and the predicate
 *      matches multiple values in the array, all matching values will be replaced.  If the above
 *      hold but 'replaceAll' is specified as 'false', only the first value will be replaced.
 *
 *      Default: false
 *      Optional
 *
 * The default behavior (when no options are provided) is to throw an error if the predicate matches
 * multiple values.
 *
 * @returns {[boolean, T[]]}
 *   An array with 2 elements, where the first element represents a boolean flag indicating whether
 *   or not a replacement was made, the second element represents the updated array (or the existing
 *   array if a replacement was not made).
 */
export const replaceInArray = <
  T extends Record<string, unknown> | string | number,
  P extends T,
  O extends ReplaceInArrayOptions<T, P>,
>(
  arr: T[],
  { insert, predicate, strictUniqueness = true, replaceAll = false }: O,
): [boolean, T[]] => {
  const replace = (arr_: T[], v: ValueAtIndex<T, P>): T[] => {
    if (arr_[v.index] === undefined) {
      throw new Error("Unexpected Condition: Index should be in the array!");
    }
    return [
      ...arr_.slice(0, v.index),
      typeof insert === "function" ? insert(v.value) : insert,
      ...arr_.slice(v.index + 1),
    ];
  };

  const indices: ValueAtIndex<T, P>[] = arr
    .map((v, i) => (predicate(v) === true ? { value: v, index: i } : null))
    .filter((i): i is ValueAtIndex<T, P> => i !== null);

  if (indices.length === 0) {
    return [false, arr];
  } else if (indices.length !== 1) {
    if (strictUniqueness) {
      throw new Error(
        `The provided predicate matched ${
          indices.length
        } values in the array at indices '${indices.join(", ")}'.`,
      );
    } else if (replaceAll === true) {
      return [
        true,
        indices.reduce(
          (prev: T[], curr: ValueAtIndex<T, P>) => replace(prev, curr),
          arr,
        ),
      ];
    }
  }
  return [true, replace(arr, indices[0])];
};
